import React, { useState } from "react";

/**
 * The row's ⋮ menu.
 *
 * Deliberately a small local component rather than an adoption of
 * `components/ui/dropdown-menu`: that primitive has two consumers in the whole
 * app, so pulling it in here would make one list page the de-facto precedent
 * for a component system nobody has committed to yet. This does the three
 * things the row needs and nothing else.
 *
 * The menu holds exactly the actions the page already had — open the tenant,
 * open the unit, delete the tenant — collected out of the row instead of
 * spread across it. No action is added: cancellation in particular belongs to
 * its own slice and its own preflight.
 */
export default function TenancyRowActions({ items, label = "Aktionen" }) {
  const [open, setOpen] = useState(false);
  const visible = (items || []).filter(Boolean);
  if (visible.length === 0) return null;

  return (
    <div className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex h-[26px] w-[26px] items-center justify-center rounded-[6px] text-[15px] leading-none text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        ⋮
      </button>

      {open ? (
        <>
          {/* Click-away layer. A plain button so Escape-less dismissal still
              works with the keyboard and screen readers skip it. */}
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            className="fixed inset-0 z-40 cursor-default"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div
            role="menu"
            className="absolute right-0 z-50 mt-1 min-w-[176px] overflow-hidden rounded-[8px] border border-border bg-card py-1 shadow-lg"
          >
            {visible.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  item.onSelect();
                }}
                className={`block w-full px-3 py-[6px] text-left text-[12.5px] hover:bg-muted ${
                  item.destructive
                    ? "text-rose-700 dark:text-rose-300"
                    : "text-foreground"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
